import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { throwError } from 'rxjs'
import { catchError, tap } from 'rxjs/operators'
import { EventsCalendarStateModel } from './events-calendar.model'
import { EventsCalendarService } from '../events-calendar.service'
import {
  CreateCalendarEvent,
  CreateCalendarTask,
  DeleteCalendarEvent,
  DeleteCalendarEventTask,
  GetCalendarEventByForm,
  GetCalendarEvents,
  GetCalendarTasks,
  GetCalendarUsers,
  GetCompletedCalendarTasks,
  LinkCalendarEventInspectionForm,
  ToggleCalendarEventTask,
  UpdateCalendarEvent,
  UpdateCalendarTask
} from './events-calendar.actions'
import {
  ICalendarEventByFormResponse,
  ICalendarEventItem,
  ICalendarUserItem,
  ITaskListItem
} from '../../interfaces/events-calendar.interface'

@State<EventsCalendarStateModel>({
  name: 'eventsCalendar'
})
@Injectable()
export class EventsCalendarState {
  constructor(private eventsCalendarService: EventsCalendarService) {}

  @Selector()
  static events(state: EventsCalendarStateModel): ICalendarEventItem[] {
    return state.eventsResponse?.events ?? []
  }

  @Selector()
  static eventByForm(state: EventsCalendarStateModel): ICalendarEventByFormResponse {
    return state.eventByForm
  }

  @Selector()
  static tasks(state: EventsCalendarStateModel): ITaskListItem[] {
    return state.tasksResponse?.tasks ?? []
  }

  @Selector()
  static completedTasks(state: EventsCalendarStateModel): ITaskListItem[] {
    return state.completedTasksResponse?.tasks ?? []
  }

  @Selector()
  static completedTasksLoaded(state: EventsCalendarStateModel): boolean {
    return !!state.completedTasksResponse
  }

  @Selector()
  static users(state: EventsCalendarStateModel): ICalendarUserItem[] {
    return state.usersResponse?.users ?? []
  }

  @Action(GetCalendarUsers, { cancelUncompleted: true })
  getCalendarUsers(ctx: StateContext<EventsCalendarStateModel>) {
    return this.eventsCalendarService
      .getCalendarUsers()
      .pipe(tap((res) => ctx.patchState({ usersResponse: res })))
  }

  @Action(GetCalendarTasks, { cancelUncompleted: true })
  getCalendarTasks(ctx: StateContext<EventsCalendarStateModel>, { payload }: GetCalendarTasks) {
    return this.eventsCalendarService
      .getTasks({ ...payload, isCompleted: false })
      .pipe(tap((res) => ctx.patchState({ tasksResponse: res })))
  }

  @Action(GetCompletedCalendarTasks, { cancelUncompleted: true })
  getCompletedCalendarTasks(
    ctx: StateContext<EventsCalendarStateModel>,
    { payload }: GetCompletedCalendarTasks
  ) {
    return this.eventsCalendarService
      .getTasks({ ...payload, isCompleted: true })
      .pipe(tap((res) => ctx.patchState({ completedTasksResponse: res })))
  }

  @Action(GetCalendarEvents, { cancelUncompleted: true })
  getCalendarEvents(ctx: StateContext<EventsCalendarStateModel>, { payload }: GetCalendarEvents) {
    return this.eventsCalendarService
      .getCalendarEvents(payload)
      .pipe(tap((res) => ctx.patchState({ eventsResponse: res })))
  }

  @Action(CreateCalendarTask)
  createCalendarTask(_: StateContext<EventsCalendarStateModel>, { payload }: CreateCalendarTask) {
    return this.eventsCalendarService.createTask(payload)
  }

  @Action(UpdateCalendarTask)
  updateCalendarTask(
    _: StateContext<EventsCalendarStateModel>,
    { taskId, payload }: UpdateCalendarTask
  ) {
    return this.eventsCalendarService.updateTask(taskId, payload)
  }

  @Action(CreateCalendarEvent)
  createCalendarEvent(_: StateContext<EventsCalendarStateModel>, { payload }: CreateCalendarEvent) {
    return this.eventsCalendarService.createCalendarEvent(payload)
  }

  @Action(UpdateCalendarEvent)
  updateCalendarEvent(
    _: StateContext<EventsCalendarStateModel>,
    { eventId, payload }: UpdateCalendarEvent
  ) {
    return this.eventsCalendarService.updateCalendarEvent(eventId, payload)
  }

  @Action(DeleteCalendarEvent)
  deleteCalendarEvent(_: StateContext<EventsCalendarStateModel>, { eventId }: DeleteCalendarEvent) {
    return this.eventsCalendarService.deleteCalendarEvent(eventId)
  }

  @Action(ToggleCalendarEventTask)
  toggleCalendarEventTask(
    ctx: StateContext<EventsCalendarStateModel>,
    { taskId, isCompleted }: ToggleCalendarEventTask
  ) {
    this.applyTaskCompletion(ctx, taskId, isCompleted)
    return this.eventsCalendarService.updateTaskStatus(taskId, isCompleted).pipe(
      catchError((err) => {
        this.applyTaskCompletion(ctx, taskId, !isCompleted)
        return throwError(() => err)
      })
    )
  }

  private static insertSorted(tasks: ITaskListItem[], task: ITaskListItem): ITaskListItem[] {
    const index = tasks.findIndex((t) => (task.dueDate ? !t.dueDate || t.dueDate > task.dueDate : false))
    return index === -1 ? [...tasks, task] : [...tasks.slice(0, index), task, ...tasks.slice(index)]
  }

  private applyTaskCompletion(
    ctx: StateContext<EventsCalendarStateModel>,
    taskId: string,
    isCompleted: boolean
  ) {
    const state = ctx.getState()
    const openTasks = state.tasksResponse?.tasks ?? []
    const completedTasks = state.completedTasksResponse?.tasks ?? []
    const item = [...openTasks, ...completedTasks].find((t) => t.id === taskId)

    if (item && item.isCompleted !== isCompleted) {
      const moved = { ...item, isCompleted }
      const remainingOpen = openTasks.filter((t) => t.id !== taskId)
      const remainingCompleted = completedTasks.filter((t) => t.id !== taskId)
      ctx.patchState({
        tasksResponse: {
          ...state.tasksResponse,
          tasks: isCompleted ? remainingOpen : EventsCalendarState.insertSorted(remainingOpen, moved)
        },
        completedTasksResponse: {
          ...state.completedTasksResponse,
          tasks: isCompleted
            ? EventsCalendarState.insertSorted(remainingCompleted, moved)
            : remainingCompleted
        }
      })
    }

    const events = state.eventsResponse?.events
    if (events?.some((e) => e.tasks.some((t) => t.id === taskId))) {
      ctx.patchState({
        eventsResponse: {
          ...state.eventsResponse,
          events: events.map((e) =>
            e.tasks.some((t) => t.id === taskId)
              ? { ...e, tasks: e.tasks.map((t) => (t.id === taskId ? { ...t, isCompleted } : t)) }
              : e
          )
        }
      })
    }
  }

  @Action(DeleteCalendarEventTask)
  deleteCalendarEventTask(
    _: StateContext<EventsCalendarStateModel>,
    { taskId }: DeleteCalendarEventTask
  ) {
    return this.eventsCalendarService.deleteTask(taskId)
  }

  @Action(LinkCalendarEventInspectionForm)
  linkCalendarEventInspectionForm(
    _: StateContext<EventsCalendarStateModel>,
    { eventId, inspectionType, formId }: LinkCalendarEventInspectionForm
  ) {
    return this.eventsCalendarService.linkInspectionForm(eventId, inspectionType, formId)
  }

  @Action(GetCalendarEventByForm)
  getCalendarEventByForm(
    ctx: StateContext<EventsCalendarStateModel>,
    { formId }: GetCalendarEventByForm
  ) {
    ctx.patchState({ eventByForm: undefined })
    return this.eventsCalendarService
      .getEventByForm(formId)
      .pipe(tap((res) => ctx.patchState({ eventByForm: res })))
  }
}
